import { AxiosResponse } from 'axios';
import { startOfYear, subYears } from 'date-fns';

import API from '../Api';
// Types.
import {
  ConsumptionAverage,
  ConsumptionRates,
  MeteringPointGroupConsumptionReport,
  ProfileCost,
  SelectedMeteringPoint,
  WeightedAvgSpotPriceItem,
} from '../types/consumptionRates';
import {
  EliqEuc,
  EliqForecast,
  EliqHomeProfile,
  EliqHomeProfileCompletion,
  EliqProfileItem,
  EliqSimilarHomes,
  EliqSimilarHomesConsumption,
  EliqSimilarHomesReport,
} from '../types/eliq';
import { Contract } from '../types/contract';
// Utils.
import { getUserToken, logUserOut } from '../utils/login';

import { controller } from '../utils/abortController';

// View period
// All: returns yearly data if range longer than 1 year
// Year: returns monthly data if range longer than 1 month
// Month: returns daily values
// Day: returns hourly values

export const fetchConsumptionRange = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  viewPeriod = 'Year',
  start = '',
  end = '',
): Promise<ConsumptionRates | undefined> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (!start) {
    start = startOfYear(subYears(new Date(), 1)).toISOString();
  }
  if (!end) {
    end = new Date().toISOString();
  }

  if (meteringPointId === '' || meteringPointId === null) {
    return undefined;
  }

  const consumptionRates: AxiosResponse<ConsumptionRates> | undefined = await API({
    signal: controller.signal,
    method: 'post',
    url: `/v1/consumption/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}?version=2&start=${start}&end=${end}&viewPeriod=${viewPeriod}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
    },
  }).catch(() => undefined);

  return consumptionRates?.data;
};

export const fetchEliqEuc = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  start: string,
  stop: string,
): Promise<EliqEuc | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const eliqEuc: AxiosResponse<EliqEuc> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqEuc/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}/?start=${start}&stop=${stop}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch(() => undefined);

    if (!eliqEuc?.data.breakdown) {
      return false;
    } else {
      return eliqEuc.data;
    }
  }
  return false;
};

export const fetchEliqForecast = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  start: string,
  stop: string,
  resolution: string,
): Promise<EliqForecast | undefined> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const eliqForecast: AxiosResponse<EliqForecast> | undefined = await API({
    signal: controller.signal,
    method: 'post',
    url: `/v2/eliqForecast/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}/?start=${start}&stop=${stop}&resolution=${resolution}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
    },
  }).catch(() => undefined);

  return eliqForecast?.data;
};

export const fetchEliqHomeProfile = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
): Promise<EliqHomeProfile | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const eliqHomeProfile: AxiosResponse<EliqHomeProfile> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqHomeProfile/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch(() => undefined);

    if (!eliqHomeProfile?.data.properties) {
      return false;
    } else {
      return eliqHomeProfile.data;
    }
  }
  return false;
};
export const patchEliqHomeProfile = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  homeprofile: EliqHomeProfile,
  path?: string,
  value?: number | string | string[] | EliqProfileItem[],
): Promise<number | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const eliqHomeProfile: AxiosResponse<number> = await API({
      method: 'patch',
      url: `/v2/eliqHomeProfile/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        homeprofile: JSON.stringify(homeprofile),
        content: `${content}`,
        path,
        value: JSON.stringify(value),
      },
    });

    return eliqHomeProfile.data;
  }
  return false;
};

export const fetchEliqHomeProfileCompletion = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
): Promise<EliqHomeProfileCompletion | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const eliqHomeProfileCompletion: AxiosResponse<EliqHomeProfileCompletion> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqHomeProfileCompletion/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch(() => undefined);

    if (!eliqHomeProfileCompletion?.data) {
      return false;
    } else {
      return eliqHomeProfileCompletion.data;
    }
  }
  return false;
};

export const fetchEliqSimilarHomes = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
): Promise<EliqSimilarHomes | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const eliqSimilarHomes: AxiosResponse<EliqSimilarHomes> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqSimilarHomes/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return undefined;
      }
    });

    if (!eliqSimilarHomes?.data.result) {
      return false;
    } else {
      return eliqSimilarHomes.data;
    }
  }
  return false;
};

export const fetchEliqSimilarHomesConsumption = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  start: string,
  stop: string,
  resolution: string,
): Promise<EliqSimilarHomesConsumption | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const EliqSimilarHomesConsumption: AxiosResponse<EliqSimilarHomesConsumption> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqSimilarHomesConsumption/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}/?start=${start}&stop=${stop}&resolution=${resolution}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return undefined;
      }
    });

    if (!EliqSimilarHomesConsumption?.data.result) {
      return false;
    } else {
      return EliqSimilarHomesConsumption.data;
    }
  }
  return false;
};

export const fetchEliqSimilarHomesReport = async (
  dataOwnerCustomerId: number,
  actingCustomerId: number,
  meteringPointId: string,
  start: string,
  stop: string,
): Promise<EliqSimilarHomesReport | false> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  if (meteringPointId !== '') {
    const EliqSimilarHomesReport: AxiosResponse<EliqSimilarHomesReport> | undefined = await API({
      signal: controller.signal,
      method: 'post',
      url: `/v2/eliqSimilarHomesReport/${dataOwnerCustomerId}/${actingCustomerId}/${meteringPointId}/?start=${start}&stop=${stop}`,
      headers: {
        Authorization: `Bearer ${bearer}`,
      },
      data: {
        content,
      },
    }).catch((error) => {
      if (error.response) {
        if (error.response.status === 401) {
          logUserOut();
        }
        return undefined;
      }
    });

    if (!EliqSimilarHomesReport?.data.result) {
      return false;
    } else {
      return EliqSimilarHomesReport.data;
    }
  }
  return false;
};

export const fetchProfileCost = async (
  contract: Contract,
  actingCustomerId: number,
  fromDate: string,
  toDate: string,
): Promise<ProfileCost[] | undefined> => {
  const { bearer, content } = getUserToken();

  const profileCost: AxiosResponse<ProfileCost[]> | undefined = await API({
    signal: controller.signal,
    method: 'post',
    url: `/v1/profileCost/${contract.customerId}/${actingCustomerId}/${contract.contractCode}?start=${fromDate}&end=${toDate}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return undefined;
    }
  });

  return profileCost?.data;
};

export const fetchMeteringPointGroupConsumption = async (
  dataOwnerCustomerId: number,
  meteringPointGroupId: string,
  start: string,
  end: string,
  viewPeriod: string,
): Promise<ConsumptionRates | undefined> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const consumptionRates: AxiosResponse<ConsumptionRates> | undefined = await API({
    signal: controller.signal,
    method: 'post',
    url: `/v1/meteringPointGroupConsumption/${dataOwnerCustomerId}/${meteringPointGroupId}?start=${start}&end=${end}&viewPeriod=${viewPeriod}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return undefined;
    }
  });

  return consumptionRates?.data;
};

export const createMeteringPointGroupConsumptionReport = async (
  dataOwnerCustomerId: number,
  meteringPointGroupId: string,
  start: string,
  end: string,
  viewPeriod: string,
): Promise<number> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const createReport: AxiosResponse<number> = await API({
    method: 'post',
    url: `/v1/meteringPointGroupConsumptionReport/${dataOwnerCustomerId}/${meteringPointGroupId}?start=${start}&end=${end}&viewPeriod=${viewPeriod}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
      request: 'create',
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return createReport.data;
};
export const fetchMeteringPointGroupConsumptionReports = async (
  dataOwnerCustomerId: number,
  meteringPointGroupId: string,
): Promise<MeteringPointGroupConsumptionReport[]> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const consumptionReports: AxiosResponse<MeteringPointGroupConsumptionReport[]> = await API({
    method: 'post',
    url: `/v1/meteringPointGroupConsumptionReport/${dataOwnerCustomerId}/${meteringPointGroupId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
      request: 'reports',
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return consumptionReports.data;
};
export const fetchMeteringPointGroupConsumptionReportUrl = async (
  dataOwnerCustomerId: number,
  meteringPointGroupId: string,
  identifier: string,
): Promise<string> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const consumptionReportUrl: AxiosResponse<string> = await API({
    method: 'post',
    url: `/v1/meteringPointGroupConsumptionReport/${dataOwnerCustomerId}/${meteringPointGroupId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
      request: 'url',
      identifier,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return consumptionReportUrl.data;
};
export const deleteMeteringPointGroupConsumptionReport = async (
  dataOwnerCustomerId: number,
  meteringPointGroupId: string,
  identifier: string,
): Promise<number> => {
  const { bearer, content } = getUserToken();

  if (!bearer || bearer.length === 0) {
    logUserOut();
  }

  const deleteReport: AxiosResponse<number> = await API({
    method: 'delete',
    url: `/v1/meteringPointGroupConsumptionReport/${dataOwnerCustomerId}/${meteringPointGroupId}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
    data: {
      content,
      identifier,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return error.response;
    }
  });

  return deleteReport.data;
};

export const fetchWeightedAverage = async (
  selectedMeteringPoint: SelectedMeteringPoint | undefined,
  scope: 'landing' | 'full' | 'period',
  actingCustomerId: number,
  startDate?: Date,
): Promise<WeightedAvgSpotPriceItem[] | undefined> => {
  const { bearer } = getUserToken();

  if (selectedMeteringPoint === undefined) {
    return undefined;
  }
  const weightedAvg: AxiosResponse<WeightedAvgSpotPriceItem[]> | undefined = await API({
    signal: controller.signal,
    method: 'get',
    url: `/v1/weightedAvgSpotPrice/${scope}/${selectedMeteringPoint.customerId}/${actingCustomerId}/${selectedMeteringPoint.contractNo}${
      scope === 'period' && startDate ? `?year=${startDate.getFullYear()}&month=${startDate.getMonth() + 1}` : ''
    }`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return undefined;
    }
  });

  return weightedAvg?.data;
};

export const fetchConsumptionAverage = async (
  selectedMeteringPoint: SelectedMeteringPoint | undefined,
  actingCustomerId: number,
  start: Date,
  end: Date,
  viewPeriod: string,
): Promise<ConsumptionAverage | undefined> => {
  const { bearer } = getUserToken();

  if (selectedMeteringPoint === undefined) {
    return undefined;
  }
  const consumptionAverage: AxiosResponse<ConsumptionAverage> | undefined = await API({
    signal: controller.signal,
    method: 'get',
    url: `/v2/consumptionAverage/Sales/${selectedMeteringPoint.customerId}/${actingCustomerId}/${
      selectedMeteringPoint.contractNo
    }?start=${start?.toISOString()}&end=${end?.toISOString()}&viewPeriod=${viewPeriod}`,
    headers: {
      Authorization: `Bearer ${bearer}`,
    },
  }).catch((error) => {
    if (error.response) {
      if (error.response.status === 401) {
        logUserOut();
      }
      return undefined;
    }
  });

  return consumptionAverage?.data;
};
